import { ModalCloseButton, ModalShell } from '@busy/ui';
import { useT } from '../lib/i18n';
import { useUI } from '../store/ui';
import {
  WHATS_NEW_WINDOW_HOURS,
  type ChangelogEntry,
  type ChangelogKind,
} from '../lib/changelog';

// "What's New" — once-a-day popup for beta testers showing major features /
// fixes shipped in the last WHATS_NEW_WINDOW_HOURS. Sits on top of Today
// after the daily quote splash dismisses. Two ways out: the X (close) and a
// "Proceed to Today" button at the bottom — both fire onDismiss.
//
// Layout:
//   - Dark navy header with the Busy mascot tile + "What's new" eyebrow
//   - Light card body with one card per entry: kind badge + title + body +
//     "shipped X ago" timestamp
//   - Sticky footer: full-width "Continue to today" button

export function WhatsNewModal({
  entries,
  onDismiss,
}: {
  entries: ChangelogEntry[];
  onDismiss: () => void;
}) {
  const t = useT();
  const language = useUI((s) => s.language);
  const dateLocale = language === 'es' ? 'es-US' : 'en-US';

  return (
    <ModalShell
      open
      onClose={onDismiss}
      size="md"
      ariaLabel={t('whatsnew.aria')}
    >
      <div className="flex flex-col max-h-[88dvh]">
        {/* Header — dark navy, mascot tile + eyebrow + heading. The X is
            absolutely positioned over this band via ModalCloseButton's
            'dark' variant so it reads against the navy. */}
        <div className="shrink-0 bg-navy-900 text-white px-6 pt-6 pb-5 rounded-t-2xl relative">
          <ModalCloseButton onClose={onDismiss} variant="dark" />
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl overflow-hidden bg-navy-800 grid place-items-center shrink-0">
              <img
                src="/assets/busy-mascot.png"
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <div className="text-[11px] tracking-[0.18em] uppercase font-extrabold text-gold-400 mb-0.5">
                {t('whatsnew.eyebrow')}
              </div>
              <h2 className="font-display text-2xl leading-tight">
                {t('whatsnew.title')}
              </h2>
              <p className="text-[12px] text-white/60 mt-0.5">
                {t('whatsnew.windowSub', { hours: WHATS_NEW_WINDOW_HOURS })}
              </p>
            </div>
          </div>
        </div>

        {/* Scrollable list of entries. */}
        <div className="flex-1 overflow-y-auto min-h-0 bg-bg-alt p-4 space-y-3">
          {entries.map((e) => (
            <EntryCard key={e.id} entry={e} dateLocale={dateLocale} />
          ))}
        </div>

        {/* Sticky footer — primary action confirms + dismisses. */}
        <div className="shrink-0 border-t border-line bg-surface px-4 py-3">
          <button
            type="button"
            onClick={onDismiss}
            className="w-full h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold"
          >
            {t('whatsnew.proceed')}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function EntryCard({
  entry,
  dateLocale,
}: {
  entry: ChangelogEntry;
  dateLocale: string;
}) {
  const t = useT();
  const ago = formatTimeAgo(entry.shippedAt, t, dateLocale);
  return (
    <div className="rounded-xl border border-line bg-surface p-3.5">
      <div className="flex items-center gap-2 mb-1.5">
        <KindBadge kind={entry.kind} />
        <span className="text-[10px] tracking-[0.14em] uppercase font-bold text-ink-500 tabular-nums">
          {ago}
        </span>
      </div>
      <strong className="block text-[15px] leading-tight mb-1">
        {t(entry.titleKey)}
      </strong>
      <p className="text-[13px] text-ink-700 leading-snug">
        {t(entry.bodyKey)}
      </p>
    </div>
  );
}

function KindBadge({ kind }: { kind: ChangelogKind }) {
  const t = useT();
  const cls: Record<ChangelogKind, string> = {
    feature:     'bg-gold-500/15 text-gold-700 dark:text-gold-300 border-gold-500/40',
    improvement: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/40',
    fix:         'bg-green-600/15 text-green-700 dark:text-green-300 border-green-600/40',
  };
  return (
    <span
      className={`inline-flex items-center gap-1 py-0.5 pr-1.5 rounded-md border text-[10px] tracking-[0.12em] uppercase font-extrabold ${cls[kind]}`}
      style={{ paddingLeft: 'calc(0.375rem + 0.12em)' }}
    >
      {kind === 'feature' && <SparkleIcon className="w-2.5 h-2.5" />}
      {kind === 'improvement' && <ArrowUpIcon className="w-2.5 h-2.5" />}
      {kind === 'fix' && <CheckIcon className="w-2.5 h-2.5" />}
      {t(`whatsnew.kind.${kind}`)}
    </span>
  );
}

// "2h ago" / "Yesterday" / explicit date for older entries that slipped past
// the 24h mark but still within the window. t() handles localization.
function formatTimeAgo(iso: string, t: (k: string, v?: Record<string, string | number>) => string, dateLocale: string): string {
  const ms = Date.now() - Date.parse(iso);
  const minutes = Math.floor(ms / (60 * 1000));
  const hours = Math.floor(ms / (60 * 60 * 1000));
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  if (minutes < 1) return t('whatsnew.ago.justNow');
  if (minutes < 60) return t('whatsnew.ago.minutes', { n: minutes });
  if (hours < 24) return t('whatsnew.ago.hours', { n: hours });
  if (days === 1) return t('whatsnew.ago.yesterday');
  if (days < 7) return t('whatsnew.ago.days', { n: days });
  return new Date(iso).toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' });
}

function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12 2l1.8 5.4L19 9l-5.2 1.6L12 16l-1.8-5.4L5 9l5.2-1.6L12 2z" />
    </svg>
  );
}
function ArrowUpIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="12" y1="19" x2="12" y2="5" />
      <polyline points="5 12 12 5 19 12" />
    </svg>
  );
}
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
